import { Badge, Box, HStack, Text, Tooltip, VStack } from "@chakra-ui/react";
import { STAGES, STAGE_LABELS } from "./placingBook";

/** Colours for the four stages (submission → bound). */
export const STAGE_COLOURS = { submission: "blue.500", quotation: "teal.400", order: "teal.600", bound: "green.500" };

const TONES = {
  action: { colorScheme: "orange" },
  waiting: { colorScheme: "gray" },
  done: { colorScheme: "green" },
  info: { colorScheme: "blue" },
};

/** Four dots for the stages, filled up to the current one, with its name and summary. */
export const StageDots = ({ summary }) => (
  <VStack align="start" spacing={0.5}>
    <HStack spacing={2}>
      <HStack spacing={1}>
        {STAGES.map((stage, i) => (
          <Box key={stage} w="16px" h="5px" borderRadius="full" bg={i <= summary.stageIndex ? STAGE_COLOURS[summary.stage] : "gray.200"} />
        ))}
      </HStack>
      <Text fontSize="sm">{STAGE_LABELS[summary.stage]}</Text>
    </HStack>
    <Text fontSize="xs" color="gray.500">{summary.stageSummary}</Text>
  </VStack>
);

/** Written or signed share of the whole risk, as a bar with a 100% mark. */
export const MarketBar = ({ market, width = "70px" }) => {
  if (!market) return <Text color="gray.400">—</Text>;
  const over = market.percent > 100;
  const colour = market.percent < 100 && market.label === "written" ? "orange.400" : "teal.500";
  return (
    <Tooltip label={`${market.percent}% ${market.label}`} hasArrow>
      <HStack spacing={2}>
        <Box position="relative" w={width} h="5px" bg="gray.200" borderRadius="full">
          <Box h="100%" w={`${Math.min(market.percent, 100)}%`} bg={colour} borderRadius="full" />
          {over && <Box position="absolute" right="-2px" top="-3px" w="2px" h="11px" bg="gray.700" />}
        </Box>
        <Text fontSize="xs" fontFamily="mono" fontWeight="semibold">{market.percent}%</Text>
      </HStack>
    </Tooltip>
  );
};

/** What happens next on a placement; orange when it needs you. */
export const NextChip = ({ next }) => (
  <Badge {...TONES[next.tone]} variant="subtle" borderRadius="full" px={2.5} py={0.5} textTransform="none" fontWeight="medium">
    {next.label}
  </Badge>
);

/** "2h ago", "today", "yesterday" or a short date. */
export const relativeTime = (value) => {
  const raw = value?.$date ?? value;
  if (!raw) return "—";
  const date = new Date(raw);
  const ms = Date.now() - date.getTime();
  const hours = ms / 3600000;
  if (hours >= 0 && hours < 1) return "just now";
  if (hours >= 0 && hours < 12) return `${Math.floor(hours)}h ago`;
  const days = Math.floor((new Date().setHours(0, 0, 0, 0) - new Date(date).setHours(0, 0, 0, 0)) / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  return shortDate(raw);
};

export const shortDate = (value) => {
  const raw = value?.$date ?? value;
  return raw ? new Date(raw).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "—";
};

/** Short date, red when it has passed. */
export const DueDate = ({ value, isOpen = true }) => {
  const raw = value?.$date ?? value;
  if (!raw) return <Text color="gray.400">—</Text>;
  const overdue = isOpen && new Date(raw).getTime() < Date.now();
  const soon = isOpen && new Date(raw).getTime() - Date.now() < 3 * 86400000;
  return <Text color={overdue || soon ? "red.500" : undefined} fontWeight={overdue ? "semibold" : undefined}>{shortDate(raw)}</Text>;
};

/** A tile with a coloured top edge, as on the placing landing pages. */
export const SummaryTile = ({ label, value, help, colour = "gray.300", onClick, isActive }) => (
  <Box
    borderWidth="1px"
    borderTopWidth="3px"
    borderTopColor={colour}
    borderRadius="md"
    bg="whiteAlpha.800"
    p={4}
    cursor={onClick ? "pointer" : undefined}
    boxShadow={isActive ? "outline" : undefined}
    _hover={onClick ? { boxShadow: "sm" } : undefined}
    onClick={onClick}>
    <Text fontSize="sm" color="gray.600">{label}</Text>
    <Text fontSize="3xl" fontWeight="semibold" lineHeight="1.2">{value}</Text>
    {help && <Text fontSize="xs" color="gray.500">{help}</Text>}
  </Box>
);
